import WebStorage from './WebStorage'
import CloudManager from './CloudManager'
import {convertECPostData} from './ECData'
import {config} from '../config'

class CustomManager {
  constructor () {
    /**
     * デバイスIDの保存名（key）
     * @type {string}
     * @private
     */
    this.DEVICE_ID_KEY = 'deviceID'

    /**
     * WebStorageのインスタンス
     * @type {object}
     * @private
     */
    this.webStorage = new WebStorage()

    /**
     * CloudManagerのインスタンス
     * @type {object}
     * @private
     */
    this.cloudMgr = new CloudManager()

    this.config = config
    this.storageURL = 'https://firebasestorage.googleapis.com/v0/b/' + this.config.firebaseConfig.storageBucket + '/o/'

    // デバイスIDを取得
    this.setDeviceID()
  }

  /**
   * @private
   * デバイスIDを作成して返す ※参考:https://github.com/GoogleChrome/chrome-platform-analytics/blob/master/src/internal/identifier.js
   * @return {string} xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   */
  generateDeviceID () {
    let chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('')
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case 'x':
          chars[i] = Math.floor(Math.random() * 16).toString(16)
          break
        case 'y':
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16)
          break
      }
    }
    return chars.join('')
  }

  /**
   * @private
   * customIDを作成して返す ※参考:https://www.mrbinr.com/2014/07/03/uniqid-en-javascript
   * @return {string} 'cba9-561b-2921'
   */
  generateCustomID () {
    let customID = (new Date().getTime() + Math.floor((Math.random() * 100000000000) + 1)).toString(12)
    const separateCustomID = customID.match(/(.{4})/g).join('-')

    return separateCustomID
  }

  /**
   * @private
   * DeviceIDの有無を確認して、存在しない場合deviceID発行、deviceIDはStorageへ保存する ※初期化のタイミングで実行されるため、通常このメソッドを明示的に呼ぶ必要は無い
   */
  setDeviceID () {
    if (!this.webStorage.hasData(this.DEVICE_ID_KEY)) {
      this.webStorage.setData(this.DEVICE_ID_KEY, this.generateDeviceID())
    }
  }

  /**
   * DeviceIDを返す
   * @return {string} xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   */
  getDeviceID () {
    if (this.webStorage.hasData(this.DEVICE_ID_KEY)) {
      return this.webStorage.getData(this.DEVICE_ID_KEY)
    } else {
      return null
    }
  }

  /**
   * @private
   * 製品データ内の画像パス全てにstorageURLを追加付与
   * @param  {object} data 置き換え対象のデータ（製品データを想定）
   * @return {object}
   */
  getReplaceStorageURLs (data) {
    if (typeof data === 'undefined') return false

    if (data['mainImage'] !== '' && typeof data['mainImage'] !== 'undefined') {
      const convertMainImageURLs = data['mainImage'].map(element => {
        return this.generateStorageURL(element)
      })
      data['mainImage'] = convertMainImageURLs
    }

    if (data['brandMainImage'] !== '' && typeof data['brandMainImage'] !== 'undefined') {
      data['brandMainImage'] = this.generateStorageURL(data['brandMainImage'])
    }

    if (typeof data['customImage'] !== 'undefined') {
      data['customImage'].forEach(image => {
        image['filename'] = this.generateStorageURL(image['filename'], 'custom')
      })
    }

    if (data['parts'] !== '' && typeof data['parts'] !== 'undefined') {
      data['parts'].forEach((part) => {
        // items用
        if (typeof part['selects'] !== 'undefined') {
          part['selects'].forEach((select) => {
            // selects内データScene画像のURL書き換え
            if (typeof select['thumbnail'] !== 'undefined') {
              select['thumbnail'] = this.generateStorageURL(select['thumbnail'])
            }

            if (typeof select['scene'] !== 'undefined') {
              select['scene'].forEach(element => {
                element['image'] = this.generateStorageURL(element['image'])
              })
            }
          })
          // customItem用
        } else if (typeof part['scene'] !== 'undefined') {
          if (typeof part['thumbnail'] !== 'undefined') {
            part['thumbnail'] = this.generateStorageURL(part['thumbnail'])
          }

          part['scene'].forEach((element) => {
            element['image'] = this.generateStorageURL(element['image'])
          })
        }

        // Partにマスクデータが入っている場合の処理
        if (typeof part['mask'] !== 'undefined') {
          part['mask'].forEach((element) => {
            element['image'] = this.generateStorageURL(element['image'])
          })
        }
      })
    }

    return data
  }

  /**
   * @private
   * storageの画像URLを生成
   * ※firestorageのgetDownloadURLでは時間がかかるため
   * @param  {string} filename 画像ファイル名
   * @param  {string} path 格納パス
   * @return {string} storageBaseURL firestorageのURLを付与した文字列を返す
   */
  generateStorageURL (filename, path = 'item') {
    let storageBaseURL = ''

    if (filename !== '' && filename !== null) {
      storageBaseURL = this.storageURL + path + '%2F' // firebaseだと/が%2Fにエスケープされるため
      storageBaseURL += encodeURIComponent(filename) + '?alt=media'
    }

    return storageBaseURL
  }

  /**
   * アイテムのIDから探索して商品データを返す
   * @param  {string} id WordPressで振られたユニークID
   * @param {string} 呼び出し元を"web"か"mirror"で指定
   * @return {object} 商品データ（Promise形式で返却）
   */
  getItem (id, from = null) {
    return new Promise((resolve, reject) => {
      if (id === '') reject(new Error('not id defined'))

      this.cloudMgr.getItem(id).then((data) => {
        if (typeof data !== 'undefined') {
          // parts配列からgcdのみを抽出
          let replaceData = this.getReplaceStorageURLs(data)

          if (from === 'web' && replaceData.disabledWeb) {
            reject(new Error('This item is not shown in Web.'))
          }
          if (from === 'mirror' && replaceData.disabledMirror) {
            reject(new Error('This item is not shown in Mirror.'))
          }

          resolve(replaceData)
        } else {
          reject(new Error('something bad happened'))
        }
      }).catch(() => {
        reject(new Error('not item defined'))
      })
    })
  }

  /**
   * brandIDに一致する全ての商品データを返す
   * @param  {int} brandID ブランドID
   * @param {string} 呼び出し元を"web"か"mirror"で指定
   * @return {array} 商品データ（Promise形式で返却）
   */
  getAllItems (brandID, from = null) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getAllItems().then((data) => {
        let products = []

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            const object = data[key]

            if (object['brandID'] === brandID) {
              const replaceData = this.getReplaceStorageURLs(object)
              products.push(replaceData)
            }
          }
        }

        if (from === 'web') {
          products = products.filter((el) => !el.disabledWeb)
        }
        if (from === 'mirror') {
          products = products.filter((el) => !el.disabledMirror)
        }

        resolve(products)
      })
    })
  }

  /**
   * brandIDに一致するブランドデータを返す
   * @param  {int} brandID ブランドID
   * @return {object} ブランドデータ（Promise形式で返却）
   */
  getBrand (brandID) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getAllBrands().then((data) => {
        data[brandID]['mainImage'] = this.generateStorageURL(data[brandID]['mainImage'])
        resolve(data[brandID])
      })
    })
  }

  /**
   * 全てのブランドデータを返す
   * @param {string} 呼び出し元を"web"か"mirror"で指定
   * @return {array} ブランドデータ（Promise形式で返却）
   */
  getAllBrands (from = null) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getAllBrands().then((data) => {
        let results = []

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            data[key]['mainImage'] = this.generateStorageURL(data[key]['mainImage'])
            results.push(data[key])
          }
        }

        results.sort((a, b) => {
          if (a.order === 0 && b.order === 0) return 0
          if (a.order === 0 && b.order !== 0) return 1
          if (a.order !== 0 && b.order === 0) return -1
          return a.order - b.order
        })

        if (from === 'web') {
          results = results.filter((el) => !el.disabledWeb)
        }
        if (from === 'mirror') {
          results = results.filter((el) => !el.disabledMirror)
        }

        resolve(results)
      })
    })
  }

  /**
   * カスタマイズIDに紐づけて、カスタム商品データを保存する
   * @param  {object} json 保存するデータオブジェクト
   */
  setCustomItem (json, isSaveImages = true) {
    let txtJson = JSON.stringify(json)

    const firebaseURLExp = new RegExp(this.storageURL, 'g')
    const queryExp = new RegExp(/\?alt=media/, 'g')
    const itemPathExp = new RegExp(/item%2F/, 'g') // item用書換
    const customItemPathExp = new RegExp(/custom%2F/, 'g') // customItem用書換

    const replaceTxtJson = txtJson.replace(firebaseURLExp, '')
      .replace(queryExp, '')
      .replace(itemPathExp, '')
      .replace(customItemPathExp, '')

    const replaceJson = JSON.parse(replaceTxtJson)
    const images = replaceJson['customImage']
    const replaceImages = []
    const imagesLoadpromises = []

    if (isSaveImages === true) {
      // Image->blob化用のPromise配列の作成
      images.forEach(element => {
        const imageLoadObj = new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('GET', [element['src']], true)
          xhr.responseType = 'arraybuffer'

          xhr.onload = () => {
            if (xhr.readyState === 4 && xhr.status === 200) {
              const arrayBufferView = new Uint8Array(xhr.response)
              const blob = new Blob([arrayBufferView], {type: 'image/png'})
              resolve(blob)
            } else {
              reject(new Error(xhr.statusText))
            }
          }
          xhr.onerror = () => {
            reject(new Error(xhr.statusText))
          }
          xhr.send(null)
        })

        imagesLoadpromises.push(imageLoadObj)
      })
    }

    return new Promise((resolve, reject) => {
      if (isSaveImages === true) { // 画像保存の時
        Promise.all(imagesLoadpromises).then((blobs) => {
          images.forEach((image, index) => {
            const pushData = {}
            pushData['filename'] = json['customID'] + '-' + index + '.png'
            pushData['src'] = blobs[index]
            pushData['label'] = image['label']

            image['filename'] = pushData['filename'] // jsonにファイル名を追加
            delete image['src'] // base64データの削除

            replaceImages.push(pushData)
          })
        }).then(() => {
          this.cloudMgr.setCustomItem(replaceJson)
            .then(() => {
              this.cloudMgr.putCustomItemImages(replaceImages).then(() => {
                resolve()
              })
            })
            .catch((error) => {
              reject(error)
            })
        }).catch((error) => {
          reject(error)
        })
      } else { // jsonを保存するだけ
        this.cloudMgr.setCustomItem(replaceJson).then(() => {
          resolve()
        }).catch((error) => {
          reject(error)
        })
      }
    })
  }

  /**
   * カスタマイズIDに一致するカスタム商品のデータを複製
   * @param  {string} customID カスタマイズID
   */
  duplicateCustomItem (customID) {
    return new Promise((resolve, reject) => {
      this.getCustomItem(customID).then((customItem) => {
        customItem['customID'] = this.generateCustomID()
        return customItem
      }).then((json) => {
        this.setCustomItem(json, false).then(() => {
          resolve()
        }).catch((error) => {
          reject(new Error(error))
        })
      })
    })
  }

  /**
   * カスタマイズIDに一致したカスタム商品データを返す
   * @param {string} ggcd 商品管理番号
   * @param {string} gcd 商品番号
   * @return {Object} カスタム商品データ（Promise形式で返却）
   */
  getCustomItem (customID) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getCustomItem(customID).then((data) => {
        if (typeof data['customID'] !== 'undefined') {
          // アプリ側で読み取るためにfirebase databaseのURLを付与
          const replaceData = this.getReplaceStorageURLs(data)
          resolve(replaceData)
        } else {
          reject(new Error('something bad happened'))
        }
      })
        .catch(() => {
          reject(new Error('no bad happened'))
        })
    })
  }

  /**
   * ggcdとgcdを元に、カスタム商品データを返す
   * ECからカスタム画面に遷移した時のみ使用
   * @param {string} ggcd 商品管理番号
   * @param {string} gcd 商品番号
   * @return {Object} カスタム商品データ（Promise形式で返却）
   */
  getNewCustomItemFromEC (ggcd, gcd) {
    return new Promise((resolve, reject) => {
      // 値のチェック
      if (
        typeof ggcd === 'undefined' ||
        ggcd === '' ||
        typeof gcd === 'undefined' ||
        ggcd === '') {
        reject(new Error('Value of ggcd or gcd is empty'))
      }

      this.cloudMgr.getAllItems().then((data) => {
        let targetItem = ''

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            const element = data[key]

            element['parts'].forEach(part => {
              for (let index = 0; index < part['selects'].length; index++) {
                const select = part['selects'][index]
                if (select['ggcd'] === ggcd && select['gcd'] === gcd) {
                  targetItem = element
                  break
                }
              }
            })
          }
        }

        if (targetItem !== '') {
          const emptyCustomItem = this.generateEmptyCustomItem(targetItem, ggcd, gcd)
          const replaceData = this.getReplaceStorageURLs(emptyCustomItem)

          resolve(replaceData)
        } else {
          reject(new Error('No result item'))
        }
      })
    })
  }

  generateEmptyCustomItem (item, ggcd = '', gcd = '') {
    const customItem = this.getEmptyCustomItemTemplete(item)

    // parts内探索
    item['parts'].forEach((part) => {
      const selects = part['selects']
      const initalPartsNumber = part['initalPartsNumber']

      selects.forEach((select, index) => {
        if ((select['ggcd'] === ggcd && select['gcd'] === gcd) || index === initalPartsNumber) {
          select['label'] = part['label']
          select['id'] = part['id']
          select['type'] = part['type']
          select['baseItem'] = part['baseItem']
          select['ratio'] = part['ratio']
          select['layer'] = part['layer']
          select['isEC'] = part['isEC']

          customItem['parts'].push(select)
        }
      })
    })

    // ggcd, gcdが複数入っているケースを想定して、フィルタリング
    if (customItem['parts'].length > 1) {
      const diffParts = customItem['parts'].filter((element, index, array) => {
        if (element['baseItem'] === true && element['ggcd'] === ggcd && element['gcd'] === gcd) {
          return element
        } else if (element['baseItem'] !== true) {
          return element
        }
      })
      customItem['parts'] = diffParts
    }

    return customItem
  }

  /**
   * ggcdとgcdを元に、カスタム商品データを返す
   * @param {string} id itemのユニークID
   * @return {Object} カスタム商品データ（Promise形式で返却）
   */
  getNewCustomItemFromApp (id) {
    return new Promise((resolve, reject) => {
      // 値のチェック
      if (typeof id === 'undefined' || id === '') {
        reject(new Error('Value of id is empty'))
      }

      this.getItem(id).then((item) => {
        // 空のcustomItemを作成
        const customItem = this.getEmptyCustomItemTemplete(item)

        item['parts'].forEach(element => {
          if (element['initalPartsNumber'] !== -1) {
            const initalPartsNumber = element['initalPartsNumber']

            // initalPartsNumberで指定したデータが入っている時のみデータを格納
            if (typeof element['selects'][initalPartsNumber] !== 'undefined') {
              const select = element['selects'][initalPartsNumber]

              // partsからset
              select['label'] = element['label']
              select['id'] = element['id']
              select['type'] = element['type']
              select['baseItem'] = element['baseItem']
              select['ratio'] = element['ratio']
              select['layer'] = element['layer']
              select['isEC'] = element['isEC']

              customItem['parts'].push(select)
            }
          }
        })
        resolve(customItem)
      }).catch(() => {
        reject(new Error('no bad happened'))
      })
    })
  }

  getEmptyCustomItemTemplete (item) {
    return {
      'id': item['id'],
      'customID': this.generateCustomID(),
      'name': item['name'],
      'brandID': item['brandID'],
      'brandName': item['brandName'],
      'brandMainImage': item['brandMainImage'],
      'lastUpdate': new Date().getTime(),
      'deviceID': '',
      'makerID': '',
      'cartInDate': '',
      'totalPrice': '',
      'taxRate': '',
      'amount': '',
      'delieryDate': '',
      'customImage': [],
      'cartInState': false,
      'signageState': false,
      'deleteState': false,
      'parts': []
    }
  }

  /**
   * デバイスIDに紐づく全てのカスタマイズ商品データを返す
   * @return {array} カスタマイズ商品データ（Promise形式で返却）
   */
  getAllCustomItems () {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getAllCustomItems().then((data) => {
        const selfDeviceID = this.getDeviceID()
        let results = []
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            const object = data[key]
            if (object['deviceID'] === selfDeviceID) {
              const replaceData = this.getReplaceStorageURLs(object)
              results.push(replaceData)
            }
          }
        }
        resolve(results)
      }).catch(error => {
        reject(error)
      })
    })
  }

  /**
   * customItemのデータ部分書き換え
   * @param {String} customID 対象のカスタムID
   * @param {String} key セットしたいkey
   * @param {String} value セットしたいvalue
   * @return {any} 書き換え完了検知用Promiseデータ
   */
  setCustomItemValue (customID, key, value) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.setCustomItemValue(customID, key, value).then(() => {
        resolve(true)
      }).catch(() => {
        reject(new Error('Failed set signage state'))
      })
    })
  }

  /**
   * customItemの部分データ取得
   * @param {string} customID 対象のカスタムID
   * @param {String} key 取得したいvalueのkry
   * @return {bool} フラグ（Promise形式で返却）
   */
  getCustomItemValue (customID, key) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getCustomItemValue(customID, key).then((data) => {
        resolve(data)
      })
    })
  }

  /**
   * カスタマイズIDに一致したワンタイムパスワードのデータを返す
   * @return {Object} カスタム商品データ（Promise形式で返却）
   */
  getOneTimePasswordData (password) {
    return new Promise((resolve, reject) => {
      this.cloudMgr.getAllOneTimePasswordsData().then((data) => {
        resolve(data)
      })
    })
  }

  /**
   * デバイスIDをKeyにワンタイムパスワードとカスタムIDを保存する
   * @return {Object} カスタム商品データ（Promise形式で返却）
   */
  setOneTimePasswordData (customID, password) {
    const deviceID = this.getDeviceID()
    return new Promise((resolve, reject) => {
      this.cloudMgr.setOneTimePasswordData(deviceID, customID, password).then(() => {
        resolve(true)
      })
    })
  }

  /**
   * カスタマイズIDに一致したワンタイムパスワードのデータを削除
   * @return {Object} カスタム商品データ（Promise形式で返却）
   */
  removeOneTimePasswordData () {
    const deviceID = this.getDeviceID()
    return new Promise((resolve, reject) => {
      this.cloudMgr.removeOneTimePasswordData(deviceID).then(() => {
        resolve(true)
      })
    })
  }

  /**
   */
  getAllOneTimePasswordsData () {
  }

  /**
   * EC投稿用データの作成
   * @param {any} customItem 対象のカスタムデータ
   * @return {any} E-STORESにPOSTするためのデータ
   */
  getECPostData (customItem) {
    return convertECPostData(customItem)
  }

  setStorage (key, value) {
    this.webStorage.setData(key, value)
  }

  getStorage (key) {
    return this.webStorage.getData(key)
  }
}

const customMgr = new CustomManager()
export default customMgr



// WEBPACK FOOTER //
// ./src/modules/CustomManager.js